import React from "react";

/**
 * Utility functions for the CreateTable component
 */

/**
 * Process JSON data from API and create column definitions
 *
 * @param {Array} jsonData - The data fetched from API
 * @param {Array} dropDownData - Dropdown options for table names
 * @param {Array} resTemplateData - Template configuration data
 * @param {Function} setColumns - State setter for table columns
 * @param {Function} setData - State setter for table data
 * @param {Function} setFieldTypeMap - State setter for field type mapping
 * @param {Function} setDropdownOptions - State setter for dropdown options
 * @param {Function} setSourceTableColumnName - State setter for source table column name
 */
export const processJsonData = (
  jsonData,
  dropDownData,
  resTemplateData,
  setColumns,
  setData,
  setFieldTypeMap,
  setDropdownOptions,
  setSourceTableColumnName,
  updateRow
) => {
  if (jsonData.length === 0) return;

  // Define system columns to exclude (case-insensitive)
  const systemColumns = [
    "UserMappingFileDetailsId",
    "UserMappingFileId",
    "TemplateId",
    "IsActive",
    "usermappingfiledetailsid",
    "usermappingfileid",
    "templateid",
    "isactive",
  ];

  // Filter out system columns from raw headers
  const rawHeaders = Object.keys(jsonData[0]).filter(
    (header) =>
      !systemColumns.some(
        (sysCol) => sysCol.toLowerCase() === header.toLowerCase()
      )
  );

  // Add deduplication logic for mandatory and inscope fields
  const deduplicatedHeaders = [];
  const seenColumnTypes = new Set();

  rawHeaders.forEach((header) => {
    const lowerHeader = header.toLowerCase();

    // Check for mandatory field variations
    if (lowerHeader.includes("mandatory")) {
      if (!seenColumnTypes.has("mandatory")) {
        seenColumnTypes.add("mandatory");
        deduplicatedHeaders.push(header);
      }
    }
    // Check for inscope field variations
    else if (lowerHeader.includes("scope")) {
      if (!seenColumnTypes.has("inscope")) {
        seenColumnTypes.add("inscope");
        deduplicatedHeaders.push(header);
      }
    }
    // For other columns, add them normally
    else {
      deduplicatedHeaders.push(header);
    }
  });

  // ADD THIS NEW CODE - Rename comments_1 to Technical Comments
const renamedHeaders = deduplicatedHeaders.map(header => {
  if (header.toLowerCase() === 'comments_1' || header === 'comments_1') {
    return 'Technical Comments';
  }
  return header;
});

  const newFieldTypeMap = new Map(
    resTemplateData.map((field) => [field.excelColumnName, field])
  );
  setFieldTypeMap(newFieldTypeMap);
  setDropdownOptions(dropDownData);

  // Create column definitions based on field types
 const dataColumns = renamedHeaders.map((col, index) => {
  const originalCol = deduplicatedHeaders[index];
  const fieldConfig = newFieldTypeMap.get(originalCol);
    // Check if this column should be a checkbox (based on field name)
    const isCheckboxField =
      col.toLowerCase().includes("mandatory") ||
      col.toLowerCase().includes("inscope") ||
      col.toLowerCase().includes("in-scope") ||
      col.toLowerCase().includes("in scope") ||
      (fieldConfig && fieldConfig.controlType === "Checkbox");

    if (isCheckboxField) {
      return {
        accessorKey: originalCol,
        header: col,
        editType: "checkbox",
        size: 100,
        columncategoryname: fieldConfig?.columncategoryname || "",
        cell: ({ row }) => {
          // Normalize the value handling to account for all possible formats
          const value = row.original[col];
          const isChecked =
            value === true ||
            value === "true" ||
            value === "Yes" ||
            value === "Y" ||
            value === "y" ||
            value === 1 ||
            value === "1";

          return (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                if (typeof updateRow === "function") {
                  const newValue = e.target.checked ? "Y" : "N";
                  updateRow(
                    row,
                    col,
                    newValue,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    (prevData) => {
                      const newData = [...prevData];
                      newData[row.index] = {
                        ...row.original,
                        [col]: newValue,
                      };
                      return newData;
                    }
                  );
                }
              }}
              style={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
                margin: "auto",
                display: "block",
              }}
            />
          );
        },
      };
    } else if (
      newFieldTypeMap.has(col) &&
      fieldConfig.dbColumnName === "sourcetablename"
    ) {
      setSourceTableColumnName(col);
      return {
        accessorKey: col,
        fieldName: "STN",
        header: col,
        editType: "dropdown",
        options: dropDownData,
        size: 120,
        columncategoryname: fieldConfig?.columncategoryname || "",
      };
    } else if (
      newFieldTypeMap.has(col) &&
      fieldConfig.dbColumnName === "sourcefieldname"
    ) {
      return {
        accessorKey: col,
        fieldName: "SFN",
        header: col,
        editType: "dropdown",
        options: [],
        size: 120,
        columncategoryname: fieldConfig?.columncategoryname || "",
      };
    } else if (newFieldTypeMap.has(col) && fieldConfig.controlType === "Text") {
      return {
        accessorKey: col,
        header: col,
        editType: "text",
        size: 120,
        columncategoryname: fieldConfig?.columncategoryname || "",
      };
   } else {
  // Define specific editable columns
  const editableColumns = [
    'comments',
    'data cleansing considerations', 
    'source field description',
    'view field name',
    'sourcefielddescription', // in case it's without spaces
    'viewfieldname' // in case it's without spaces
  ];
  
  const lowerColName = col.toLowerCase().replace(/\s+/g, '').replace(/[-_]/g, '');
  const isEditableColumn = editableColumns.some(editableCol => {
    const normalizedEditableCol = editableCol.toLowerCase().replace(/\s+/g, '').replace(/[-_]/g, '');
    return lowerColName === normalizedEditableCol || lowerColName.includes(normalizedEditableCol);
  });
  
  if (isEditableColumn) {
    return {
      accessorKey: col,
      header: col,
      editType: "text",
      size: 120,
      columncategoryname: fieldConfig?.columncategoryname || "",
    };
  } else {
    return {
      accessorFn: (row) => row[col],
      id: col,
      header: col,
      size: 120,
      columncategoryname: fieldConfig?.columncategoryname || "",
    };
  }
}
  });

  const allColumns = [...dataColumns];
  setColumns(allColumns);
  setData([...jsonData]);
};

/**
 * Custom global filter function for the table
 *
 * @param {Object} row - Row object
 * @param {string} columnId - Column ID
 * @param {string} filterValue - Filter value to search for
 * @returns {boolean} Whether row matches filter
 */
export const globalFilterFn = (row, columnId, filterValue) => {
  return row
    .getAllCells()
    .some((cell) =>
      String(cell.getValue()).toLowerCase().includes(filterValue.toLowerCase())
    );
};

/**
 * Handle column resize start event
 *
 * @param {Event} e - Mouse event
 * @param {Object} header - Header object
 * @param {Function} setResizingColumn - State setter for resizing column
 * @param {Object} columnSizes - Current column sizes
 * @param {Function} setColumnSizes - State setter for column sizes
 */
export const handleColumnResizeStart = (
  e,
  header,
  setResizingColumn,
  columnSizes,
  setColumnSizes
) => {
  e.preventDefault();
  setResizingColumn(header.id);

  const startX = e.pageX;
  const startWidth = columnSizes[header.id] || header.column.getSize();

  const handleMouseMove = (e) => {
    const newWidth = Math.max(startWidth + (e.pageX - startX), 50); // Minimum width: 50px
    setColumnSizes((prev) => ({ ...prev, [header.id]: newWidth }));
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    setResizingColumn(null);
  };

  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
};

/**
 * Calculate table height based on pagination size
 *
 * @param {Object} pagination - Pagination object with pageSize
 * @returns {string} CSS height value
 */
export const calculateTableHeight = (pagination) => {
  const headerHeight = 36; // Reduced header row height
  const rowHeight = 36; // Reduced row height
  const desiredRows = pagination.pageSize; // Number of rows we want to display
  return headerHeight + rowHeight * desiredRows + "px";
};

/**
 * Handle page size change for pagination
 *
 * @param {Event} event - Change event
 * @param {Object} pagination - Current pagination state
 * @param {Function} setPagination - Pagination state setter
 * @param {Object} table - Table instance
 */
export const handlePageSizeChange = (
  event,
  pagination,
  setPagination,
  table
) => {
  const newSize = Number(event.target.value);
  setPagination((prev) => {
    // Calculate the new page index while maintaining the position
    const newPageIndex = Math.floor((prev.pageIndex * prev.pageSize) / newSize);
    return { ...prev, pageSize: newSize, pageIndex: newPageIndex };
  });
  table.setPageSize(newSize);
};

/**
 * Update a row with a new value, handling dependent field updates
 *
 * @param {Object} row - Row to update
 * @param {string} columnHeader - Column header to update
 * @param {any} updatedValue - New value
 * @param {string} fieldName - Field name (STN, SFN)
 * @param {Map} fieldTypeMap - Field type mapping
 * @param {Object} dependentValues - Dependent values for dropdown options
 * @param {string} sourceTableColumnName - Source table column name
 * @param {Function} fetchDependentValues - Function to fetch dependent values
 * @param {Function} setData - State setter for table data
 * @param {Function} setLocalValue - State setter for local input values
 * @param {Function} updateData - Function to update data in backend
 * @returns {Promise<void>}
 */
export const updateRow = async (
  row,
  columnHeader,
  updatedValue,
  fieldName,
  fieldTypeMap,
  dependentValues,
  sourceTableColumnName,
  fetchDependentValues,
  setData,
  setLocalValue,
  updateData
) => {
  const previousValue = row.original[columnHeader];
  if (updatedValue !== previousValue) {
    let updatedRow = {
      ...row.original,
      [columnHeader]: updatedValue,
    };

    if (fieldName === "STN") {
      await fetchDependentValues(updatedValue);
      fieldTypeMap.forEach((fieldConfig) => {
        // Field to update and its value
        let fieldToUpdate = "";
        let fieldToUpdateValue = "";

        // Check for specific dbColumnNames and set corresponding values
        if (fieldConfig.dbColumnName === "sourcefieldname") {
          fieldToUpdate = fieldConfig.excelColumnName;
          fieldToUpdateValue = "";
        } else if (fieldConfig.dbColumnName === "sourcedatatype") {
          fieldToUpdate = fieldConfig.excelColumnName;
          fieldToUpdateValue = "";
        }

        // Update the row with the new values
        if (fieldToUpdate) {
          updatedRow = {
            ...updatedRow,
            [fieldToUpdate]: fieldToUpdateValue,
          };
        }
      });
    } else if (fieldName === "SFN") {
      const selectedColumn = dependentValues[
        row.original[sourceTableColumnName]
      ]?.find((col) => col.columnName === updatedValue);

      if (selectedColumn) {
        // Iterate over all fields in the fieldTypeMap
        fieldTypeMap.forEach((fieldConfig) => {
          // Field to update and its value
          let fieldToUpdate = "";
          let fieldToUpdateValue = "";

          // Check for specific dbColumnNames and set corresponding values
          if (fieldConfig.dbColumnName === "sourcedatatype") {
            fieldToUpdate = fieldConfig.excelColumnName;
            fieldToUpdateValue = selectedColumn.columnDataType;
          }

          // Update the row with the new values
          if (fieldToUpdate && fieldToUpdateValue) {
            updatedRow = {
              ...updatedRow,
              [fieldToUpdate]: fieldToUpdateValue,
            };
          }
        });
      }
    }

    // Optimistically update the UI
    setData((prevData) => {
      const newData = [...prevData];
      newData[row.index] = updatedRow; // Replace row with updated one
      return newData;
    });

    // Call API to persist the change
    try {
      await updateData(updatedRow);
    } catch (error) {
      // Optionally: revert the change if API fails
      setData((prevData) => {
        const newData = [...prevData];
        newData[row.index] = row.original; // revert to previous row
        return newData;
      });
      // Optionally: show error to user
    }

    setLocalValue((prev) => {
      const updated = { ...prev };
      delete updated[`${row.index}-${columnHeader}`];
      return updated;
    });
  }
};

/**
 * Prepare data for backend update
 *
 * @param {Object} rowData - Row data to update
 * @param {number} fileID - File ID
 * @param {Map} dbFieldTypeMap - Database field type mapping
 * @returns {Object} Formatted data for API
 */
export const prepareUpdateData = (rowData, fileID, dbFieldTypeMap) => {
  // Template for post data
  const postDataTemplate = {
    updateMapperDetailsRequests: [
      {
        userMappingFileDetailsId: 0,
        transformationrule: "string",
        sourcefield: "string",
        crossreferencetable: "string",
        defaultvalue: "string",
        conversionrule: "string",
        reusablelogic: "string",
        criticalerrorhandling: "string",
        comments: "string",
        sourcegroupview: "string",
        sourcetablename: "string",
        sourcefieldname: "string",
        sourcefielddescription: "string",
        sourcedatatype: "string",
        sourcedatalength: "string",
        viewfieldname: "string",
        inscope: "string",
        mandatoryfeild: "string",
        targetstructure: "string",
        fieldname: "string",
        length: "string",
        type: "string",
        decimalscale: 0,
      },
    ],
    userMappingFileId: 0,
  };

  // Map the response data to match excelColumnName
  const mappedData = postDataTemplate.updateMapperDetailsRequests.map((row) => {
    const newRow = {
      userMappingFileDetailsId: rowData.UserMappingFileDetailsId,
    };
    Object.entries(row).forEach(([key, value]) => {
      if (dbFieldTypeMap.has(key)) {
        newRow[key] = rowData[dbFieldTypeMap.get(key)];
      }
    });
    return newRow;
  });

  // Add userMappingFileId outside the object
  const finalData = {
    userMappingFileId: fileID,
    updateMapperDetailsRequests: mappedData,
  };

  return finalData;
};

/**
 * Custom styles for react-select to make it more compact
 */
export const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "28px",
    height: "28px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "28px",
    padding: "0 6px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "28px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "4px",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "4px",
  }),
};

/**
 * Get dropdown select styles with customizations
 *
 * @param {Object} additionalStyles - Additional styles to merge
 * @returns {Object} Merged styles
 */
export const getDropdownStyles = (additionalStyles = {}) => {
  return {
    ...customSelectStyles,
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (provided) => ({
      ...provided,
      minWidth: "250px", // Add this line
      width: "max-content", // Add this line
    }),

    control: (provided) => ({
      ...provided,
      width: "100%",
      minHeight: "24px",
      height: "24px",
      fontSize: "12px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "24px",
      padding: "0 4px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "12px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "24px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "12px",
      padding: "4px 8px",
      whiteSpace: "nowrap", // Add this line to prevent text wrapping
    }),
    ...additionalStyles,
  };
};
